import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  CircleDollarSign,
  ClockAlert,
  PiggyBank,
  Printer,
  ShoppingBag,
  Ticket,
  Wallet,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type {
  DashboardCountAmount,
  DashboardNowResponse,
  DashboardSalesResponse,
  DashboardSalesTotals,
} from '@aeris/contracts'
import { parseAmount } from '@aeris/finance'
import { ZERO_QUANTITY, formatQuantity, parseQuantity } from '@aeris/inventory'
import { formatMoney, subtract, toNumber, toString } from '@aeris/money'
import {
  Badge,
  Button,
  LoadFailed,
  Section,
  Spinner,
  StatTile,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  cn,
} from '@aeris/ui'

import { fetchNowDashboard, fetchSalesDashboard } from '../lib/api/dashboard.ts'
import { fetchBranches } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import {
  dashboardSummaryPrintFor,
  receivedCash,
} from '../lib/dashboard-summary-print.ts'
import {
  customRange,
  exclusiveEnd,
  isPeriodChoice,
  periodRange,
  type PeriodChoice,
} from '../lib/period-range.ts'
import {
  useCompanyHeader,
  useReceiptTemplate,
} from '../lib/use-receipt-template.ts'
import { useWorkspace, type WorkspaceModule } from '../lib/workspace.ts'

import { ColumnChart, ShareBar } from './dashboard-charts.tsx'
import { KpiTile, Trend } from './dashboard-kit.tsx'
import { PeriodPicker } from './period-picker.tsx'
import { PrintDocumentDialog } from './print-document-dialog.tsx'
import { ReceivedEntriesDialog } from './received-entries-dialog.tsx'
import { VoucherOverview } from './voucher-overview.tsx'

/**
 * O painel da loja (F134): o Início de quem dirige.
 *
 * O dono pediu *"métricas de vendas do dia, orçamentos, funil de vendas,
 * quanto cada vendedor vendeu, ver do dia, semana, mês, ontem, selecionar
 * período… quantas trocas, quantos vales, quantas cobranças vão vencer, quanto
 * o pessoal está devendo, quantos produtos em baixa de estoque, quantos pedidos
 * o funcionário deixou esquecidos"*.
 *
 * A ordem é a de leitura dele (F219: *"tem que parecer um dashboard… o
 * principal está escondido"*): os números grandes no topo — faturamento,
 * lucro, vendas, recebido, vencido e vale em aberto —, logo abaixo o que
 * ficou parado, e depois o detalhe: vendas, vales loja × gestão antiga,
 * dinheiro e estoque, vendedores e funil, o que mais sai e o que mais volta.
 *
 * Cada bloco diz "não carregou" quando não carregou. Um zero que é na verdade
 * uma falha é o pior número que um painel pode mostrar, porque ninguém
 * desconfia dele.
 */

/*
 * O seletor de período virou componente na F158 (`period-picker.tsx`), porque
 * os relatórios fazem a mesma pergunta. O que ficou aqui é a PERSISTÊNCIA: o
 * painel guarda a escolha em `localStorage` porque é preferência de quem abre a
 * tela toda manhã; o relatório guarda na URL porque se manda por link.
 */
type Choice = PeriodChoice

const STORAGE_KEY = 'aeris.dashboard.period'

const readChoice = (): Choice => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return isPeriodChoice(stored) ? stored : 'today'
  } catch {
    return 'today'
  }
}

const saveChoice = (choice: Choice): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, choice)
  } catch {
    // Sem armazenamento (janela anônima, bloqueio): o painel abre em "Hoje".
  }
}

const EVERY_MINUTE = 60_000

const clock = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
})

const dayMonth = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
})

const hourOnly = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit' })

const brl = (amount: string): string => formatMoney(parseAmount(amount))

const plural = (count: number, one: string, many: string): string =>
  `${String(count)} ${count === 1 ? one : many}`

const FORGOTTEN: Record<
  DashboardNowResponse['forgotten'][number]['kind'],
  {
    one: string
    many: string
    detail: string
    module: WorkspaceModule
    action: string
  }
> = {
  quote_stalled: {
    one: 'orçamento parado',
    many: 'orçamentos parados',
    detail: 'enviados ou aceitos, e ninguém mexe há mais de 2 dias',
    module: 'orcamentos',
    action: 'Ver orçamentos',
  },
  draft_stalled: {
    one: 'venda começada e não terminada',
    many: 'vendas começadas e não terminadas',
    detail: 'em rascunho há mais de 1 dia',
    module: 'vendas',
    action: 'Ver vendas',
  },
  order_unfulfilled: {
    one: 'pedido confirmado que não saiu',
    many: 'pedidos confirmados que não saíram',
    detail: 'confirmados há mais de 1 dia',
    module: 'vendas',
    action: 'Ver pedidos',
  },
  delivery_open: {
    one: 'entrega na rua sem acerto',
    many: 'entregas na rua sem acerto',
    detail: 'despachadas há mais de 1 dia e ainda não acertadas',
    module: 'entregas',
    action: 'Ver entregas',
  },
  exchange_requested: {
    one: 'troca pedida esperando',
    many: 'trocas pedidas esperando',
    detail: 'pedidas há mais de 2 horas e o trocador não atendeu',
    module: 'trocas',
    action: 'Ver trocas',
  },
  channel_order_stuck: {
    one: 'pedido de marketplace parado',
    many: 'pedidos de marketplace parados',
    detail: 'o comprador já pagou lá fora; aceite para baixar o estoque',
    module: 'pedidos-canal',
    action: 'Ver pedidos',
  },
  till_left_open: {
    one: 'caixa esquecido aberto',
    many: 'caixas esquecidos abertos',
    detail: 'abertos antes de hoje e não fechados',
    module: 'caixa',
    action: 'Ver caixa',
  },
}

const dayFormat = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

/** "30/09/2026", ou "01/09/2026 a 30/09/2026" (F217, para o papel). */
const rangeLabel = (range: { from: string; to: string }): string => {
  const from = dayFormat.format(new Date(range.from))
  const to = dayFormat.format(new Date(range.to))
  return from === to ? from : `${from} a ${to}`
}

const Block = ({
  title,
  description,
  action,
  query,
  what,
  children,
  className,
}: {
  title: string
  description?: string
  action?: ReactNode
  query: { isPending: boolean; isError: boolean; refetch: () => unknown }
  what: string
  children: () => ReactNode
  className?: string
}): ReactNode => (
  <Section
    title={title}
    level={2}
    {...(description === undefined ? {} : { description })}
    {...(action === undefined ? {} : { action })}
    className={cn('bg-(--surface)', className)}
  >
    {query.isPending ? (
      <Spinner label={`Carregando ${what}`} />
    ) : query.isError ? (
      <LoadFailed
        what={what}
        onRetry={() => {
          void query.refetch()
        }}
      />
    ) : (
      children()
    )}
  </Section>
)

const Figure = ({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string
  value: string
  hint?: ReactNode
  tone?: 'neutral' | 'danger' | 'warning'
}): ReactNode => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <span className="text-(length:--text-xs) text-(--text-muted)">{label}</span>
    <span
      className={cn(
        'font-(--font-mono) text-(length:--text-md) tabular-nums',
        tone === 'danger'
          ? 'text-(--danger)'
          : tone === 'warning'
            ? 'text-(--warning)'
            : 'text-(--text-primary)'
      )}
    >
      {value}
    </span>
    {hint === undefined ? null : (
      <span className="text-(length:--text-xs) text-(--text-secondary)">
        {hint}
      </span>
    )}
  </div>
)

const countAmount = (entry: DashboardCountAmount): string =>
  entry.count === 0 ? 'nenhum' : `${String(entry.count)} · ${brl(entry.amount)}`

/** `"75.7"` → `75,7%`. */
const percentLabel = (value: string): string => `${value.replace('.', ',')}%`

/**
 * O detalhe das vendas, abaixo do gráfico: o que explica os números grandes.
 *
 * Custo e lucro médio são da F161 — o dono: *"hoje vendeu 10 telas do iPhone,
 * custo dela 50, deu 500 de custo"*. O custo é o do DIA DA VENDA; o que é
 * anterior à gravação do custo entra pelo custo médio de hoje, e o cartão diz
 * quantos. Peça sem custo nenhum fica fora da conta — e o cartão avisa, porque
 * ela faz o lucro parecer maior do que é.
 */
const DetailTiles = ({
  sales,
  previous,
}: {
  sales: DashboardSalesTotals
  previous: DashboardSalesTotals
}): ReactNode => {
  const warnings = [
    sales.costEstimatedLines === 0
      ? null
      : `${String(sales.costEstimatedLines)} ${sales.costEstimatedLines === 1 ? 'item' : 'itens'} com custo estimado`,
    sales.costMissingLines === 0
      ? null
      : `${String(sales.costMissingLines)} sem custo cadastrado`,
  ].filter((entry): entry is string => entry !== null)

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      <StatTile
        label="Ticket médio"
        value={brl(sales.averageTicket)}
        hint={`${brl(previous.averageTicket)} no período anterior`}
      />
      <StatTile
        label="Custo"
        value={brl(sales.cost)}
        hint={
          warnings.length === 0
            ? 'o custo de cada peça no dia da venda'
            : warnings.join(' · ')
        }
      />
      <StatTile
        label="Lucro médio"
        value={brl(sales.averageProfit)}
        hint={`por venda · ${brl(previous.averageProfit)} no período anterior`}
      />
      <StatTile
        label="Custo médio"
        value={brl(sales.averageCost)}
        hint="por venda"
      />
      <StatTile
        label="Descontos dados"
        value={brl(sales.discountTotal)}
        hint="diferença entre o preço cheio e o vendido"
      />
      <StatTile
        label="Canceladas"
        value={String(sales.cancelled.count)}
        hint={
          sales.cancelled.count === 0
            ? 'nenhuma venda cancelada'
            : `${brl(sales.cancelled.amount)} que não entraram`
        }
      />
    </div>
  )
}

const seriesPoints = (
  data: DashboardSalesResponse
): Parameters<typeof ColumnChart>[0]['points'] =>
  data.series.map((point) => {
    const start = new Date(point.start)
    return {
      key: point.start,
      label:
        data.period.bucket === 'hour'
          ? `${hourOnly.format(start)}h`
          : dayMonth.format(start),
      value: toNumber(parseAmount(point.revenue)),
      description: `${brl(point.revenue)} · ${plural(point.count, 'venda', 'vendas')}`,
    }
  })

export const StoreDashboard = (): ReactNode => {
  const { allows, session } = useAuth()
  const workspace = useWorkspace()
  const go = (module: WorkspaceModule, entityId?: string): void => {
    workspace.open(entityId === undefined ? { module } : { module, entityId })
  }

  const [choice, setChoice] = useState<Choice>(readChoice)
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [branchId, setBranchId] = useState('')
  const [showingReceived, setShowingReceived] = useState(false)
  // O resumo do período no papel (F236).
  const [printingSummary, setPrintingSummary] = useState(false)
  const company = useCompanyHeader()
  const template = useReceiptTemplate()

  const range =
    choice === 'custom'
      ? customRange(customFrom, customTo)
      : periodRange(choice)

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: ({ signal }) => fetchBranches(undefined, signal),
    enabled: allows('organization.read'),
  })
  const branchList = branches.data?.branches ?? []
  const branch = branchId.length === 0 ? undefined : branchId

  // O período que ainda está acontecendo se atualiza sozinho; ontem não muda.
  const live = range !== null && new Date(range.to) >= new Date()

  const sales = useQuery({
    queryKey: ['dashboard', 'sales', range?.from, range?.to, branch],
    queryFn: ({ signal }) =>
      fetchSalesDashboard(
        {
          from: range?.from ?? '',
          to: exclusiveEnd(range?.to ?? ''),
          branchId: branch,
        },
        signal
      ),
    enabled: range !== null,
    placeholderData: keepPreviousData,
    refetchInterval: live ? EVERY_MINUTE : false,
  })

  const now = useQuery({
    queryKey: ['dashboard', 'now', branch],
    queryFn: ({ signal }) => fetchNowDashboard({ branchId: branch }, signal),
    refetchInterval: EVERY_MINUTE,
  })

  const salesQuery = {
    isPending: range !== null && sales.isPending,
    isError: sales.isError,
    refetch: sales.refetch,
  }

  const attention: {
    key: string
    title: string
    detail: string
    action: string
    onAct: () => void
  }[] = []

  if (now.data !== undefined) {
    for (const entry of now.data.forgotten) {
      if (entry.count === 0) continue
      const meta = FORGOTTEN[entry.kind]
      const people = entry.byPerson
        .filter((person) => person.name !== null)
        .map((person) => `${person.name ?? ''} (${String(person.count)})`)
        .join(', ')
      const days =
        entry.oldestAt === null
          ? null
          : Math.floor(
              (new Date().getTime() - new Date(entry.oldestAt).getTime()) /
                86_400_000
            )
      attention.push({
        key: entry.kind,
        title: plural(entry.count, meta.one, meta.many),
        detail: [
          entry.amount === null ? null : brl(entry.amount),
          meta.detail,
          days === null || days < 1
            ? null
            : `o mais antigo há ${plural(days, 'dia', 'dias')}`,
          people.length === 0 ? null : people,
        ]
          .filter((part) => part !== null)
          .join(' · '),
        action: meta.action,
        onAct: () => {
          go(meta.module)
        },
      })
    }
    if (now.data.stock.belowMinimum > 0) {
      attention.push({
        key: 'below-minimum',
        title: plural(
          now.data.stock.belowMinimum,
          'produto abaixo do mínimo',
          'produtos abaixo do mínimo'
        ),
        detail: 'Hora de repor: o saldo passou do estoque mínimo da ficha.',
        action: 'Ver no Estoque',
        onAct: () => {
          go('estoque')
        },
      })
    }
    if (now.data.quotesExpiring.count > 0) {
      attention.push({
        key: 'quotes-expiring',
        title: plural(
          now.data.quotesExpiring.count,
          'orçamento expira em 3 dias',
          'orçamentos expiram em 3 dias'
        ),
        detail: `${brl(now.data.quotesExpiring.amount)} que ainda podem virar venda.`,
        action: 'Ver orçamentos',
        onAct: () => {
          go('orcamentos')
        },
      })
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <PeriodPicker
        quick
        choice={choice}
        onChoiceChange={(next) => {
          setChoice(next)
          saveChoice(next)
        }}
        customFrom={customFrom}
        customTo={customTo}
        onCustomFromChange={setCustomFrom}
        onCustomToChange={setCustomTo}
        branches={branchList}
        branchId={branchId}
        onBranchChange={setBranchId}
      >
        <span className="pb-2 text-(length:--text-xs) text-(--text-muted)">
          {choice === 'custom' && range === null
            ? 'Escolha as duas datas, a primeira antes da segunda.'
            : sales.dataUpdatedAt === 0
              ? null
              : `atualizado às ${clock.format(new Date(sales.dataUpdatedAt))}${session?.context?.branchName == null || branch !== undefined || branchList.length > 1 ? '' : ` · ${session.context.branchName}`}`}
        </span>
        <Button
          variant="secondary"
          size="sm"
          className="mb-1"
          leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
          disabled={sales.data === undefined || range === null}
          onClick={() => {
            setPrintingSummary(true)
          }}
        >
          Imprimir resumo
        </Button>
      </PeriodPicker>

      {/*
       * O fechamento numa conta só (F283). O Eduardo: "vendi 8.667? lucro
       * 2.933 por quê? quanto foi de custo? quanto foi de vale?". Cada tela
       * mostrava um recorte; aqui está a conta inteira, na ordem em que ele
       * pergunta: vendeu, custou, sobrou, como pagaram, o que falta.
       */}
      {sales.data === undefined ? null : <DayClosing data={sales.data} />}

      {/*
       * Os números grandes (F219). O que o dono olha primeiro, cada um levando
       * à tela onde se age sobre ele. Os do período dependem do período; o
       * vencido e o vale são de agora.
       */}
      <section
        aria-label="Resumo"
        className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
      >
        <KpiTile
          label="Faturamento"
          icon={CircleDollarSign}
          loading={salesQuery.isPending}
          error={sales.isError}
          value={
            sales.data === undefined ? null : brl(sales.data.sales.revenue)
          }
          hint={
            sales.data === undefined ? undefined : (
              <Trend
                current={sales.data.sales.revenue}
                previous={sales.data.previous.revenue}
              />
            )
          }
          onOpen={() => {
            go('vendas')
          }}
        />
        <KpiTile
          label="Lucro"
          icon={PiggyBank}
          loading={salesQuery.isPending}
          error={sales.isError}
          tone={
            sales.data?.sales.profit.startsWith('-') === true
              ? 'danger'
              : 'neutral'
          }
          value={sales.data === undefined ? null : brl(sales.data.sales.profit)}
          hint={
            sales.data === undefined
              ? undefined
              : sales.data.sales.marginPercent === null
                ? 'sem venda no período'
                : `margem de ${percentLabel(sales.data.sales.marginPercent)}`
          }
        />
        <KpiTile
          label="Vendas"
          icon={ShoppingBag}
          loading={salesQuery.isPending}
          error={sales.isError}
          value={
            sales.data === undefined ? null : String(sales.data.sales.count)
          }
          hint={
            sales.data === undefined
              ? undefined
              : `${String(sales.data.previous.count)} no período anterior`
          }
        />
        <KpiTile
          label="Recebido"
          icon={Wallet}
          loading={salesQuery.isPending}
          error={sales.isError}
          value={
            sales.data === undefined
              ? null
              : brl(receivedCash(sales.data.receivedByMethod).total)
          }
          hint={
            sales.data === undefined
              ? undefined
              : sales.data.receivedByMethod.length === 0
                ? 'nada recebido no período'
                : receivedCash(sales.data.receivedByMethod).top
          }
          {...(sales.data === undefined ||
          sales.data.receivedByMethod.length === 0
            ? {}
            : {
                onOpen: () => {
                  setShowingReceived(true)
                },
              })}
        />
        <KpiTile
          label="Vencido a receber"
          icon={ClockAlert}
          loading={now.isPending}
          error={now.isError}
          tone={
            (now.data?.receivables.overdue.count ?? 0) > 0
              ? 'danger'
              : 'neutral'
          }
          value={
            now.data === undefined
              ? null
              : brl(now.data.receivables.overdue.amount)
          }
          hint={
            now.data === undefined
              ? undefined
              : now.data.receivables.overdue.count === 0
                ? 'nada vencido'
                : `${plural(now.data.receivables.overdue.count, 'título', 'títulos')} · ${plural(now.data.receivables.overdueCustomers, 'cliente', 'clientes')}`
          }
          onOpen={() => {
            go('financeiro')
          }}
        />
        <KpiTile
          label="Vale em aberto"
          icon={Ticket}
          loading={now.isPending}
          error={now.isError}
          tone={
            (now.data === undefined
              ? 0
              : toNumber(parseAmount(now.data.vouchers.outstanding))) > 0
              ? 'warning'
              : 'neutral'
          }
          value={
            now.data === undefined ? null : brl(now.data.vouchers.outstanding)
          }
          hint={
            now.data === undefined
              ? undefined
              : `loja ${brl(toString(subtract(parseAmount(now.data.vouchers.outstanding), parseAmount(now.data.vouchers.outstandingOld))))} · gestão antiga ${brl(now.data.vouchers.outstandingOld)}`
          }
          onOpen={() => {
            go('trocas')
          }}
        />
      </section>

      <Block
        title="Precisa de atenção agora"
        description="O que ficou parado e com quem. Atualiza sozinho a cada minuto."
        query={now}
        what="o que precisa de atenção"
      >
        {() =>
          attention.length === 0 ? (
            <p className="text-(length:--text-sm) text-(--text-muted)">
              Nada esquecido agora.
            </p>
          ) : (
            <ul className="grid gap-2 lg:grid-cols-2">
              {attention.map((item) => (
                <li
                  key={item.key}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-(--radius-md) border border-(--warning) bg-(--warning-subtle) px-3 py-2"
                >
                  <div className="flex min-w-0 items-start gap-2">
                    <AlertTriangle
                      aria-hidden="true"
                      className="mt-0.5 h-4 w-4 shrink-0 text-(--warning)"
                    />
                    <div className="flex min-w-0 flex-col">
                      <span className="text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
                        {item.title}
                      </span>
                      <span className="text-(length:--text-xs) text-(--text-secondary)">
                        {item.detail}
                      </span>
                    </div>
                  </div>
                  <Button variant="secondary" size="sm" onClick={item.onAct}>
                    {item.action}
                  </Button>
                </li>
              ))}
            </ul>
          )
        }
      </Block>

      <Block title="Vendas" query={salesQuery} what="as vendas">
        {() =>
          sales.data === undefined ? null : (
            <div className="flex flex-col gap-4">
              <div className="grid gap-4 lg:grid-cols-3">
                <div className="flex flex-col gap-2 lg:col-span-2">
                  <h3 className="text-(length:--text-xs) font-(--weight-medium) text-(--text-secondary)">
                    {sales.data.period.bucket === 'hour'
                      ? 'Faturamento por hora'
                      : 'Faturamento por dia'}
                  </h3>
                  <ColumnChart
                    points={seriesPoints(sales.data)}
                    caption={
                      sales.data.period.bucket === 'hour'
                        ? 'Faturamento por hora'
                        : 'Faturamento por dia'
                    }
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-(length:--text-xs) font-(--weight-medium) text-(--text-secondary)">
                      Recebido por forma
                    </h3>
                    {/* F217: cada venda e cada baixa por trás do total. */}
                    {sales.data.receivedByMethod.length === 0 ? null : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowingReceived(true)
                        }}
                      >
                        Quem pagou
                      </Button>
                    )}
                  </div>
                  {/*
                    F214: o que ENTROU, no dia em que entrou — balcão, parcela
                    do crediário e acerto do motoboy; troco fora, estorno fora.
                    Era "Como pagaram", só com o que estava "compensado": sem
                    cartão, sem crediário e sem o dinheiro do motoboy.
                  */}
                  {sales.data.receivedByMethod.length === 0 ? (
                    <p className="text-(length:--text-sm) text-(--text-muted)">
                      Nada recebido no período.
                    </p>
                  ) : (
                    <ul className="flex flex-col gap-2">
                      {sales.data.receivedByMethod.map((method) => (
                        <li
                          key={method.paymentMethodId}
                          className="flex flex-col gap-1"
                        >
                          <span className="flex justify-between gap-2 text-(length:--text-sm)">
                            <span className="text-(--text-primary)">
                              {method.name}
                              <span className="ml-1 text-(length:--text-xs) text-(--text-muted)">
                                {method.kind === 'voucher'
                                  ? '· não é dinheiro novo'
                                  : `· ${plural(method.count, 'pagamento', 'pagamentos')}`}
                              </span>
                            </span>
                            <span className="font-(--font-mono) tabular-nums">
                              {brl(method.amount)}
                            </span>
                          </span>
                          <ShareBar
                            value={toNumber(parseAmount(method.amount))}
                            highest={toNumber(
                              parseAmount(
                                sales.data.receivedByMethod[0]?.amount ?? '0'
                              )
                            )}
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                  {sales.data.creditSales.count === 0 ? null : (
                    <p className="text-(length:--text-xs) text-(--text-muted)">
                      {`Vendido no crediário: ${brl(sales.data.creditSales.amount)} (${plural(sales.data.creditSales.count, 'venda', 'vendas')}) — entra quando o cliente paga.`}
                    </p>
                  )}
                </div>
              </div>
              <DetailTiles
                sales={sales.data.sales}
                previous={sales.data.previous}
              />
            </div>
          )
        }
      </Block>

      <Block
        title="Vales e trocas"
        description="Vale é o que a loja deve ao cliente em produto. De quem é cada lado: a loja, ou a gestão antiga."
        query={{
          isPending: salesQuery.isPending || now.isPending,
          isError: sales.isError || now.isError,
          refetch: () => {
            void sales.refetch()
            void now.refetch()
          },
        }}
        what="os vales"
        action={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              go('trocas')
            }}
          >
            Abrir Trocas
          </Button>
        }
      >
        {() =>
          sales.data === undefined || now.data === undefined ? null : (
            <VoucherOverview
              exchanges={sales.data.exchanges}
              vouchers={now.data.vouchers}
              onOpenExchanges={() => {
                go('trocas')
              }}
            />
          )
        }
      </Block>

      <div className="grid gap-4 xl:grid-cols-2">
        <Block
          title="Dinheiro"
          description="A situação agora, sem depender do período."
          query={now}
          what="o financeiro"
        >
          {() => {
            const data = now.data
            if (data === undefined) return null
            const receivables = data.receivables
            const debtorTop = toNumber(
              parseAmount(receivables.topDebtors[0]?.outstanding ?? '0')
            )
            return (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <h3 className="text-(length:--text-xs) font-(--weight-medium) text-(--text-secondary)">
                    A receber
                  </h3>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <Figure
                      label="Vencido"
                      value={brl(receivables.overdue.amount)}
                      tone={
                        receivables.overdue.count > 0 ? 'danger' : 'neutral'
                      }
                      hint={
                        receivables.overdue.count === 0
                          ? 'nada vencido'
                          : `${plural(receivables.overdue.count, 'título', 'títulos')} · ${plural(receivables.overdueCustomers, 'cliente', 'clientes')}`
                      }
                    />
                    <Figure
                      label="Vence hoje"
                      value={brl(receivables.dueToday.amount)}
                      hint={plural(
                        receivables.dueToday.count,
                        'título',
                        'títulos'
                      )}
                    />
                    <Figure
                      label="Próximos 7 dias"
                      value={brl(receivables.dueNext7Days.amount)}
                      hint={plural(
                        receivables.dueNext7Days.count,
                        'título',
                        'títulos'
                      )}
                    />
                  </div>
                </div>

                {receivables.topDebtors.length === 0 ? null : (
                  <div className="flex flex-col gap-2">
                    <h3 className="text-(length:--text-xs) font-(--weight-medium) text-(--text-secondary)">
                      Quem mais deve
                    </h3>
                    <ul className="flex flex-col gap-2">
                      {receivables.topDebtors.map((debtor) => (
                        <li
                          key={debtor.partyId}
                          className="flex flex-col gap-1"
                        >
                          <span className="flex items-center justify-between gap-2 text-(length:--text-sm)">
                            <button
                              type="button"
                              className="truncate text-left text-(--text-primary) underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-(--border-focus)"
                              onClick={() => {
                                go('cobrancas', debtor.partyId)
                              }}
                            >
                              {debtor.name}
                            </button>
                            <span className="flex items-center gap-2">
                              {debtor.oldestOverdueDays === null ? null : (
                                <Badge tone="danger">
                                  {`${plural(debtor.oldestOverdueDays, 'dia', 'dias')} de atraso`}
                                </Badge>
                              )}
                              <span className="font-(--font-mono) tabular-nums">
                                {brl(debtor.outstanding)}
                              </span>
                            </span>
                          </span>
                          <ShareBar
                            value={toNumber(parseAmount(debtor.outstanding))}
                            highest={debtorTop}
                            tone={
                              debtor.oldestOverdueDays === null
                                ? 'warning'
                                : 'danger'
                            }
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <h3 className="text-(length:--text-xs) font-(--weight-medium) text-(--text-secondary)">
                    A pagar
                  </h3>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <Figure
                      label="Vencido"
                      value={brl(data.payables.overdue.amount)}
                      hint={plural(
                        data.payables.overdue.count,
                        'conta',
                        'contas'
                      )}
                      tone={
                        data.payables.overdue.count > 0 ? 'danger' : 'neutral'
                      }
                    />
                    <Figure
                      label="Vence hoje"
                      value={brl(data.payables.dueToday.amount)}
                      hint={plural(
                        data.payables.dueToday.count,
                        'conta',
                        'contas'
                      )}
                    />
                    <Figure
                      label="Próximos 7 dias"
                      value={brl(data.payables.dueNext7Days.amount)}
                      hint={plural(
                        data.payables.dueNext7Days.count,
                        'conta',
                        'contas'
                      )}
                    />
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="self-start"
                  onClick={() => {
                    go('financeiro')
                  }}
                >
                  Abrir o Financeiro
                </Button>
              </div>
            )
          }}
        </Block>

        <Block
          title="Estoque"
          query={now}
          what="o estoque"
          action={
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                go('estoque')
              }}
            >
              Abrir o Estoque
            </Button>
          }
        >
          {() => {
            const stock = now.data?.stock
            if (stock === undefined) return null
            return (
              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Figure
                    label="Abaixo do mínimo"
                    value={String(stock.belowMinimum)}
                    tone={stock.belowMinimum > 0 ? 'warning' : 'neutral'}
                  />
                  <Figure label="Zerados" value={String(stock.zero)} />
                  <Figure
                    label="Negativos"
                    value={String(stock.negative)}
                    tone={stock.negative > 0 ? 'danger' : 'neutral'}
                  />
                  <Figure
                    label="Aguardando estoque"
                    value={String(stock.awaitingStock)}
                  />
                </div>
                {stock.belowMinimumItems.length === 0 ? (
                  <p className="text-(length:--text-xs) text-(--text-muted)">
                    {stock.belowMinimum === 0
                      ? 'Nenhum produto abaixo do mínimo. O mínimo de cada produto fica na ficha dele, aba Geral.'
                      : null}
                  </p>
                ) : (
                  <ul className="flex flex-col divide-y divide-(--border-subtle)">
                    {stock.belowMinimumItems.map((item) => (
                      <li
                        key={item.variantId}
                        className="flex items-center justify-between gap-3 py-1.5 text-(length:--text-sm)"
                      >
                        <span className="min-w-0 truncate text-(--text-primary)">
                          {item.name}
                          <span className="ml-2 font-(--font-mono) text-(length:--text-xs) text-(--text-muted)">
                            {item.sku}
                          </span>
                        </span>
                        <span className="whitespace-nowrap text-(--text-secondary)">
                          {`tem ${formatQuantity(parseQuantity(item.onHand) ?? ZERO_QUANTITY)} · mín. ${formatQuantity(parseQuantity(item.minimumStock) ?? ZERO_QUANTITY)}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )
          }}
        </Block>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Block
          title="Vendedores"
          description="Quanto cada um vendeu no período (balcão + pedido entregue), o lucro, e quanto ainda está em orçamento aberto — que não pagou."
          query={salesQuery}
          what="os vendedores"
          /*
           * O painel é uma olhada de manhã: dez linhas, sem corte, sem
           * exportar. Quem quiser o ranking inteiro, com "Outros" e total, vai
           * para Relatórios — e chega já no relatório certo (F158).
           */
          action={
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                go('relatorios', 'vendedores')
              }}
            >
              Ver relatório
            </Button>
          }
        >
          {() => {
            const people = sales.data?.bySalesperson ?? []
            if (people.length === 0) {
              return (
                <p className="text-(length:--text-sm) text-(--text-muted)">
                  Nenhuma venda nem orçamento no período.
                </p>
              )
            }
            const top = toNumber(parseAmount(people[0]?.revenue ?? '0'))
            return (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Vendedor</TableHeaderCell>
                      <TableHeaderCell numeric>Vendeu</TableHeaderCell>
                      <TableHeaderCell numeric>Vendas</TableHeaderCell>
                      <TableHeaderCell numeric>Lucro</TableHeaderCell>
                      <TableHeaderCell numeric>Em aberto</TableHeaderCell>
                      <TableHeaderCell numeric>Ticket</TableHeaderCell>
                      <TableHeaderCell numeric>Conversão</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {people.map((person) => (
                      <TableRow key={person.userId ?? 'sem-vendedor'}>
                        <TableCell className="text-(--text-primary)">
                          {person.name ?? (
                            <span className="text-(--text-muted)">
                              Sem vendedor
                            </span>
                          )}
                        </TableCell>
                        <TableCell numeric>
                          <div className="flex min-w-24 flex-col items-end gap-1">
                            {brl(person.revenue)}
                            <ShareBar
                              value={toNumber(parseAmount(person.revenue))}
                              highest={top}
                            />
                          </div>
                        </TableCell>
                        <TableCell numeric>
                          {String(person.salesCount)}
                        </TableCell>
                        <TableCell numeric>{brl(person.profit)}</TableCell>
                        <TableCell numeric>
                          {/* F283: orçamento que ainda não virou venda. */}
                          {person.openQuotes.count === 0 ? (
                            '—'
                          ) : (
                            <div className="flex flex-col items-end">
                              {brl(person.openQuotes.amount)}
                              <span className="text-(length:--text-xs) text-(--text-muted)">
                                {person.openQuotes.count === 1
                                  ? '1 orçamento'
                                  : `${String(person.openQuotes.count)} orçamentos`}
                              </span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell numeric>
                          {brl(person.averageTicket)}
                        </TableCell>
                        <TableCell numeric>
                          {person.conversionRate === null
                            ? '—'
                            : `${person.conversionRate.replace('.', ',')}% (${String(person.quotesConverted)} de ${String(person.quotesCreated)})`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          }}
        </Block>

        <Block
          title="Funil de orçamentos"
          description="Os orçamentos feitos no período, pelo que aconteceu com cada um."
          query={salesQuery}
          what="o funil"
        >
          {() => {
            const funnel = sales.data?.quoteFunnel
            if (funnel === undefined) return null
            if (funnel.created.count === 0) {
              return (
                <p className="text-(length:--text-sm) text-(--text-muted)">
                  Nenhum orçamento feito no período.
                </p>
              )
            }
            const steps = [
              { label: 'Feitos', entry: funnel.created },
              { label: 'Aceitos', entry: funnel.accepted },
              { label: 'Viraram venda', entry: funnel.converted },
            ]
            return (
              <div className="flex flex-col gap-3">
                <ol className="flex flex-col gap-2">
                  {steps.map((step) => (
                    <li key={step.label} className="flex flex-col gap-1">
                      <span className="flex justify-between gap-2 text-(length:--text-sm)">
                        <span className="text-(--text-primary)">
                          {step.label}
                        </span>
                        <span className="font-(--font-mono) tabular-nums">
                          {countAmount(step.entry)}
                        </span>
                      </span>
                      <ShareBar
                        value={step.entry.count}
                        highest={funnel.created.count}
                      />
                    </li>
                  ))}
                </ol>
                <p className="text-(length:--text-sm) text-(--text-secondary)">
                  {funnel.conversionRate === null
                    ? null
                    : `${funnel.conversionRate.replace('.', ',')}% dos orçamentos viraram venda.`}
                </p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Figure label="Em aberto" value={countAmount(funnel.open)} />
                  <Figure
                    label="Recusados"
                    value={countAmount(funnel.rejected)}
                  />
                  <Figure
                    label="Expirados"
                    value={countAmount(funnel.expired)}
                    tone={funnel.expired.count > 0 ? 'warning' : 'neutral'}
                  />
                  <Figure
                    label="Cancelados"
                    value={countAmount(funnel.cancelled)}
                  />
                </div>
              </div>
            )
          }}
        </Block>
      </div>

      <Block
        title="Mais vendidos"
        description="Os 10 produtos que mais faturaram no período."
        query={salesQuery}
        what="os mais vendidos"
        action={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              go('relatorios', 'top-produtos')
            }}
          >
            Ver relatório
          </Button>
        }
      >
        {() => {
          const products = sales.data?.topProducts ?? []
          if (products.length === 0) {
            return (
              <p className="text-(length:--text-sm) text-(--text-muted)">
                Nenhum produto vendido no período.
              </p>
            )
          }
          return (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell numeric>#</TableHeaderCell>
                    <TableHeaderCell>Produto</TableHeaderCell>
                    <TableHeaderCell>Código</TableHeaderCell>
                    <TableHeaderCell numeric>Quantidade</TableHeaderCell>
                    <TableHeaderCell numeric>Faturamento</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((product, index) => (
                    <TableRow key={`${product.productId}-${product.sku}`}>
                      <TableCell numeric className="text-(--text-muted)">
                        {String(index + 1)}
                      </TableCell>
                      <TableCell className="text-(--text-primary)">
                        {product.name}
                      </TableCell>
                      <TableCell className="font-(--font-mono)">
                        {product.sku}
                      </TableCell>
                      <TableCell numeric>
                        {formatQuantity(
                          parseQuantity(product.quantity) ?? ZERO_QUANTITY
                        )}
                      </TableCell>
                      <TableCell numeric>{brl(product.revenue)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        }}
      </Block>

      {/*
       * O que mais volta em troca (F150).
       *
       * A peça DEVOLVIDA, não a que o cliente levou: é o que aponta produto
       * ruim ou fornecedor ruim. O bloco some quando não houve troca no
       * período — uma tabela vazia todo dia vira ruído, e o dono para de olhar.
       */}
      {(sales.data?.returnedProducts ?? []).length === 0 ? null : (
        <Block
          title="O que mais volta em troca"
          description="As peças que os clientes mais devolveram no período. Produto que volta muito é produto (ou fornecedor) para revisar."
          query={salesQuery}
          what="o que mais volta"
          action={
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                go('relatorios', 'trocas')
              }}
            >
              Ver por marca e motivo
            </Button>
          }
        >
          {() => {
            const returned = sales.data?.returnedProducts ?? []
            return (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell numeric>#</TableHeaderCell>
                      <TableHeaderCell>Produto</TableHeaderCell>
                      <TableHeaderCell>Código</TableHeaderCell>
                      <TableHeaderCell numeric>Devolvido</TableHeaderCell>
                      <TableHeaderCell numeric>Trocas</TableHeaderCell>
                      <TableHeaderCell numeric>Valor</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {returned.map((product, index) => (
                      <TableRow
                        key={`${product.productId ?? 'sem-cadastro'}-${product.name}`}
                      >
                        <TableCell numeric className="text-(--text-muted)">
                          {String(index + 1)}
                        </TableCell>
                        <TableCell className="text-(--text-primary)">
                          {product.name}
                        </TableCell>
                        <TableCell className="font-(--font-mono) text-(--text-muted)">
                          {/* A peça de fora digitada (F148) não tem cadastro. */}
                          {product.sku ?? 'digitada'}
                        </TableCell>
                        <TableCell numeric>
                          {formatQuantity(
                            parseQuantity(product.quantity) ?? ZERO_QUANTITY
                          )}
                        </TableCell>
                        <TableCell numeric>
                          {String(product.exchanges)}
                        </TableCell>
                        <TableCell numeric>{brl(product.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          }}
        </Block>
      )}

      {printingSummary && sales.data !== undefined && range !== null ? (
        <PrintDocumentDialog
          document={dashboardSummaryPrintFor({
            data: sales.data,
            company,
            template,
            periodLabel: rangeLabel(range),
            branchLabel:
              branchList.find((entry) => entry.id === branch)?.name ??
              'Todas as lojas',
            branchChosen: branch !== undefined,
          })}
          description={`Resumo de ${rangeLabel(range)}`}
          initialPaper="a4"
          onClose={() => {
            setPrintingSummary(false)
          }}
        />
      ) : null}

      {showingReceived && range !== null ? (
        <ReceivedEntriesDialog
          from={range.from}
          to={exclusiveEnd(range.to)}
          branchId={branch}
          periodLabel={rangeLabel(range)}
          branchLabel={
            branchList.find((entry) => entry.id === branch)?.name ??
            'Todas as lojas'
          }
          onClose={() => {
            setShowingReceived(false)
          }}
        />
      ) : null}
    </div>
  )
}

/** Os tipos de forma de pagamento, como o dono fala. */
const KIND_LABEL: Readonly<Record<string, string>> = {
  cash: 'Dinheiro',
  pix: 'Pix',
  card_credit: 'Cartão de crédito',
  card_debit: 'Cartão de débito',
  tef: 'Cartão',
  voucher: 'Vale',
  customer_credit: 'Crediário',
  bank_slip: 'Boleto',
  bank_transfer: 'Transferência',
}

/** O fechamento do período numa conta só (F283). */
const DayClosing = ({
  data,
}: {
  readonly data: DashboardSalesResponse
}): ReactNode => {
  const { sales: totals } = data
  const paid = data.paidBySales
  return (
    <section
      aria-label="Fechamento"
      className="flex flex-col gap-2 rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-raised) p-4 text-(length:--text-sm)"
    >
      <p className="text-(length:--text-lg) font-(--weight-semibold) text-(--text-primary)">
        Vendido: {brl(totals.revenue)}{' '}
        <span className="text-(length:--text-sm) font-(--weight-normal) text-(--text-secondary)">
          em {String(totals.count)} {totals.count === 1 ? 'venda' : 'vendas'}{' '}
          (balcão + pedido entregue)
        </span>
      </p>
      <p>
        Custo dos produtos: <strong>{brl(totals.cost)}</strong> · Lucro:{' '}
        <strong>{brl(totals.profit)}</strong>
        {totals.marginPercent === null
          ? ''
          : ` (${totals.marginPercent.replace('.', ',')}%)`}
      </p>
      <p>
        Como pagaram:{' '}
        {paid.length === 0
          ? '—'
          : paid
              .map(
                (method) =>
                  `${KIND_LABEL[method.kind] ?? method.kind} ${brl(method.amount)}`
              )
              .join(' · ')}
        {data.receivableFromSales.count === 0
          ? ''
          : ` · A receber (motoboy/crediário) ${brl(data.receivableFromSales.amount)}`}
      </p>
      <p className="text-(--text-secondary)">
        Em aberto (orçamento que ainda não virou venda):{' '}
        <strong>{brl(data.quoteFunnel.open.amount)}</strong> (
        {String(data.quoteFunnel.open.count)})
      </p>
    </section>
  )
}
